import { gsap } from "gsap";
import { Application, Assets, Container, Graphics, Text, Texture } from "pixi.js";
import { Viewport } from "pixi-viewport";
import {
  BOARD_RADIUS,
  buildBoard,
  deriveSlots,
  edgeKey,
  forestNodeIds,
  hexToPixel,
  type Layout,
  type VigorZone,
} from "@fortress/shared";
import { RESOURCE_KINDS, resourceIconUrl } from "../resourceShapes";
import {
  BRIDGE_FILL_COLOR,
  BRIDGE_OUTLINE_COLOR,
  I_BEAM,
  iBeamSegments,
} from "../bridgeIBeam";

const HEX_SIZE = 26;
const NODE_RADIUS = 2.2; // small dark movement dot
const NODE_COLOR = 0x3a342c; // dark warm grey (reads on the light tiles)
const OWNER_RADIUS = 5; // home-base selection marker radius (setup phase)
const BUILD_R = 7.2; // raised owner token: larger than the flat resource icons
const ICON_R = 6; // printed-resource icon radius (the PNG art sits under the tokens)
const RES_UNDER_OFFSET = 11.5; // how far a building tucks its underlying resource toward a hex cell
// The resource PNGs carry transparent padding (the glyph fills ~45% of the box),
// so the drawn box is scaled up by this factor to keep the visible glyph ≈ ICON_R.
const ICON_TEX_PAD = 2.2;
const HIT_RADIUS = 9; // click target (independent of the visual dot)
const PADDING = HEX_SIZE * 2;
const BG_COLOR = 0xf2ecda; // parchment / tabletop background
const GRID_COLOR = 0x4a4034; // faint hex-lattice lines, drawn over the tiles
const TILE_BORDER = 0x2b2620; // dark ink outline around each tile

// Barracks aim arrows (Barracks → its target node): attack red / defend blue,
// each over a dark under-stroke so the line reads on any tile colour.
const AIM_ATTACK_COLOR = 0xd7263d;
const AIM_DEFEND_COLOR = 0x2e86ab;
const AIM_OUTLINE_COLOR = 0x14110d;

// Tile region rendering: a near-opaque, dark-bordered footprint per slot that
// drops in centre → outward on assembly so the board visibly tiles itself.
// The reveal runs on Pixi's own ticker (not GSAP) so it always animates and
// always ends fully visible.
const REGION_FILL_ALPHA = 0.95;
const REGION_LINE_ALPHA = 0.95;
const REVEAL_STEP_MS = 28; // stagger between successive tiles (centre → rim)
const REVEAL_DUR_MS = 380; // per-tile drop-in duration

// 2D ↔ 3D tilt: a vertical squash of the board (axonometric "tilt back on a
// table"). It's a real Pixi transform, so pan/zoom/clicks keep working.
const TILT_MIN_SCALE_Y = 0.56; // board height at full 3D tilt (1 = flat 2D)
const TILT_LERP = 0.16; // per-frame easing toward the target tilt
const MIN_ZOOM = 0.2; // viewport zoom limits (shared by clampZoom + the buttons)
const MAX_ZOOM = 4;
const DOUBLE_TAP_MS = 325;
const TILE_FOCUS_PADDING = 40; // screen px around a focused physical tile
const BOARD_FOCUS_PADDING = 16; // screen px around the complete assembled board

/** easeOutBack — a slight overshoot for the tile "pop". */
function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

/**
 * Base colour per vigor zone, inner → outer: summit red, crag white/grey,
 * ridgeline yellow, foothill green, forest (outermost rim) a distinct teal so
 * all five read apart. Used for both the node dots and the tile footprints.
 */
const ZONE_COLORS: Record<VigorZone, number> = {
  summit: 0xcd6a4f, // terracotta red (inner core)
  crag: 0xdcdad0, // warm light grey
  ridgeline: 0xd2b54e, // golden mustard
  foothill: 0xa6c084, // sage green
  forest: 0x77a98e, // muted teal-green (rim)
};

/** Pre-rendered resource icons (client/public/resources), cached by
 * `"kind:oneTime"` so every node draws from a single GPU upload. Populated by
 * `loadResourceTextures()` during board init, before any node is drawn. */
const RESOURCE_TEX: Record<string, Texture> = {};

/** Load the eight resource PNGs (4 kinds × permanent/one-time) into the cache.
 * Idempotent: textures already loaded are skipped. */
async function loadResourceTextures(): Promise<void> {
  await Promise.all(
    RESOURCE_KINDS.flatMap((kind) =>
      [false, true].map(async (oneTime) => {
        const key = `${kind}:${oneTime}`;
        if (RESOURCE_TEX[key]) return;
        RESOURCE_TEX[key] = await Assets.load(resourceIconUrl(kind, oneTime));
      }),
    ),
  );
}

/** Draw a printed-resource icon (centred at 0,0) into a node's Graphics as the
 * pre-rendered PNG art, so the board matches the sidebar/library icons exactly.
 * The permanent vs one-time look is baked into the texture. `r` is the icon's
 * half-size on the board; the box is padded out (ICON_TEX_PAD) for the PNG's
 * transparent margin. No-op until the textures have loaded. */
function drawResourceIcon(
  g: Graphics,
  type: string,
  oneTime: boolean,
  r = ICON_R,
  cx = 0,
  cy = 0,
): void {
  const tex = RESOURCE_TEX[`${type}:${oneTime}`];
  if (!tex) return;
  const box = r * 2 * ICON_TEX_PAD;
  g.texture(tex, 0xffffff, cx - box / 2, cy - box / 2, box, box);
}

/** Outline polygon for a player building, centred at (0,0): barracks = upright
 * triangle, mine = square, house = home base (square body + triangle roof).
 * Scout returns `null` → drawn natively as a circle. Radii are nudged so the
 * shapes read at a similar visual weight. */
function buildingOutline(kind: string, r: number): [number, number][] | null {
  switch (kind) {
    case "barracks": {
      const rr = r * 1.18; // a triangle looks small for its radius — bump it up
      return [
        [0, -rr],
        [rr * 0.866, rr * 0.5],
        [-rr * 0.866, rr * 0.5],
      ];
    }
    case "mine": {
      const s = r * 0.9; // half-side; a square reads heavy, so trim a touch
      return [
        [-s, -s],
        [s, -s],
        [s, s],
        [-s, s],
      ];
    }
    case "house": {
      // Home base: a house silhouette — square wall with a triangular roof.
      const s = r * 0.95;
      return [
        [-s, s * 0.9], // bottom-left
        [s, s * 0.9], // bottom-right
        [s, -s * 0.1], // right eave
        [0, -s * 1.15], // roof apex
        [-s, -s * 0.1], // left eave
      ];
    }
    default:
      return null; // scout → circle (disc)
  }
}

/** Draw a player building (centred at 0,0) with simple flat-shaded facets so it
 * reads as a raised, 3D token lit from the top-left — unlike the resource icons,
 * which stay flat 2D. A thin white outline rings it. The shape encodes the kind:
 * scout = circle (domed), barracks = triangle, mine = square, house = home base
 * (polygons chamfered into per-edge facets). */
function drawBuilding(g: Graphics, kind: string, color: number, r = BUILD_R): void {
  const outline = buildingOutline(kind, r);
  const rim = { color: 0xffffff, width: 1.2 }; // thin white outline
  const LX = -0.7071; // light direction (top-left)
  const LY = -0.7071;
  const ox = -r * 0.14; // top face / highlight nudged toward the light
  const oy = -r * 0.16;

  if (!outline) {
    // Scout: a domed disc — dark underside, mid body, lit cap toward the light.
    g.circle(0, 0, r).fill(shade(color, 0.62));
    g.circle(ox * 0.8, oy * 0.8, r * 0.82).fill(color);
    g.circle(ox * 1.7, oy * 1.7, r * 0.45).fill(shade(color, 1.3));
    g.circle(0, 0, r).stroke(rim);
    return;
  }

  // Barracks / mine: chamfer into one flat facet per edge. The top face is the
  // outline scaled down and nudged toward the light; each facet fills the strip
  // from an outer edge to the matching top edge, shaded by how much that edge
  // faces the light, so the token looks beveled.
  const topScale = 0.58;
  const top = outline.map(
    ([x, y]) => [x * topScale + ox, y * topScale + oy] as [number, number],
  );
  const n = outline.length;
  for (let i = 0; i < n; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % n]!;
    const ta = top[i]!;
    const tb = top[(i + 1) % n]!;
    const mx = (a[0] + b[0]) / 2; // edge midpoint ≈ outward normal direction
    const my = (a[1] + b[1]) / 2;
    const len = Math.hypot(mx, my) || 1;
    const lit = (mx / len) * LX + (my / len) * LY; // -1 (away) .. 1 (toward light)
    g.poly([a[0], a[1], b[0], b[1], tb[0], tb[1], ta[0], ta[1]]).fill(
      shade(color, 0.75 + 0.28 * lit),
    );
  }
  g.poly(top.flat()).fill(shade(color, 1.2)); // lit top face
  g.poly(outline.flat()).stroke(rim);
}

// "pointy" small hexes give the big board hexagon straight edges along the top
// and bottom of the screen (its vertices point left/right) — the tabletop
// orientation. Node ids are pure topology, so this only moves pixels.
const LAYOUT: Layout = {
  orientation: "pointy",
  size: HEX_SIZE,
  origin: { x: 0, y: 0 },
};

// Topology is deterministic from the radius, so the client builds the same
// graph (and the same node ids) the server does — see CLAUDE.md.
const BOARD = buildBoard(BOARD_RADIUS, LAYOUT);
// Slots (and the forest home-base region below) bin nodes into sectors by pixel
// ANGLE, so they must be derived in the same unit ("flat") frame the server
// uses — deriving them from the pointy render layout would group different
// nodes. Node ids are layout-independent, so the resulting ids index straight
// into the render graph above.
const TOPO_BOARD = buildBoard(BOARD_RADIUS);
const SLOTS = deriveSlots(TOPO_BOARD);
// Legal home-base region (outermost "forest" tiles), derived the same way the
// server derives it — for highlighting candidate nodes during setup.
const FOREST_NODE_IDS = forestNodeIds(TOPO_BOARD);

type Pt = { x: number; y: number };
// Which slot owns each node (last writer wins for nodes shared on a tile seam),
// so a node tap can also report its tile.
const SLOT_OF_NODE = new Map<number, string>();
for (const slot of SLOTS)
  for (const id of slot.nodeIds) SLOT_OF_NODE.set(id, slot.id);

// Direction (unit vector, board space) to tuck a resource that sits under a
// building: toward the node's lowest adjacent hex centre, so the icon peeks out
// below the token into a hex cell, clear of the node's edges (paths).
const NODE_RES_DIR: Pt[] = BOARD.nodes.map((node) => {
  let best: Pt = { x: 0, y: 1 };
  let bestY = -Infinity;
  for (const h of node.hexes) {
    const c = hexToPixel(LAYOUT, h);
    const dx = c.x - node.pixel.x;
    const dy = c.y - node.pixel.y;
    const len = Math.hypot(dx, dy) || 1;
    if (dy / len > bestY) {
      bestY = dy / len;
      best = { x: dx / len, y: dy / len };
    }
  }
  return best;
});

// Remote cursor presence: how far in the past remote cursors are rendered.
// Positions arrive in ~200ms state patches (the room's patchRate); rendering
// ~1.2 patch intervals behind the newest sample lets the ticker LERP between
// two real samples nearly all the time — constant-velocity smooth instead of
// 5Hz teleports — at the cost of a barely perceptible delay.
const CURSOR_INTERP_DELAY_MS = 240;
const CURSOR_BUFFER_MAX = 12; // plenty for the delay window at 5Hz
// On top of the buffered LERP, the drawn position chases the buffered point
// with a critically damped spring. The buffer alone is piecewise linear —
// every sample boundary is a velocity kink, and jitter in patch arrival shows
// up as speed wobble; a first-order (exponential) filter rounds positions but
// its velocity still jumps whenever the target's does. The spring keeps BOTH
// position and velocity continuous, which is what reads as "fluid". Settling
// time ≈ 4/omega (~130ms).
const CURSOR_SPRING_OMEGA = 30; // 1/s
// Integration guard: a hidden tab can hand the ticker a huge delta; past this
// just snap to the target instead of integrating through it.
const CURSOR_SNAP_DELTA_MS = 250;
// Remote cursor glyphs keep a constant on-screen size: counter-scaled against
// the viewport zoom, clamped so an extreme zoom can't blow them up/shrink them.
const CURSOR_MIN_SCALE = 0.4;
const CURSOR_MAX_SCALE = 3;

export type NodeClickHandler = (nodeId: number) => void;
export type EdgeClickHandler = (a: number, b: number) => void;
/** Cursor entered a node (`nodeId`) or left it (`null`). */
export type NodeHoverHandler = (nodeId: number | null) => void;

/** One Barracks aim, as synced from the server: the Barracks' node, its target
 * node, and the posture ("attack" | "defend") that picks the arrow colour. */
export interface AimView {
  from: number;
  to: number;
  mode: string;
}

/** One remote player's live pointer on the board, in the shared board plane
 * (hex-size units from the board centre — the wire format of `CursorState`). */
export interface RemoteCursorView {
  /** Session id (stable key for the interpolation buffer). */
  id: string;
  x: number;
  y: number;
  /** Seat colour (or the spectator grey) for the arrow + name pill. */
  color: number;
  name: string;
}

/** A tile laid into a slot, as synced from the server. */
export interface TilePlacementView {
  slot: string;
  tileId: string;
  face: string;
  rotation: number;
}

/** Per-node visual inputs the renderer merges into one drawn node. */
export interface NodeRenderState {
  /** Owner colour, or null when unowned (falls back to the zone tint). */
  ownerColor: number | null;
  /** Building kind on an owned node (`"scout"|"barracks"|"mine"`, `""` for none):
   * picks the raised token's shape (circle / triangle / square). */
  building: string;
  /** Printed resource type (`""` for none). */
  resource: string;
  /** `"permanent"` | `"oneTime"` | `""`. */
  permanence: string;
  /** True when the node's paths are severed (needs a Bridge). */
  disconnected: boolean;
  /** True if this node is a player's home base (drawn with a distinct marker). */
  isHomeBase: boolean;
  /** Fortress owner's colour when this node is part of an implemented Fortress,
   * else null. An empty fortress node is drawn with an "X" mark (as on the
   * card art); a building there keeps its token instead. */
  fortressColor: number | null;
}

export interface PixiGame {
  readonly app: Application;
  readonly viewport: Viewport;
  /** Merge new visual state into a node and redraw it. */
  setNodeState(nodeId: number, patch: Partial<NodeRenderState>): void;
  /** A node's current position in viewport (client) coordinates — accounts for
   * pan/zoom/rotation/tilt. For anchoring DOM overlays (e.g. the fortress
   * detail popover) to a board node. Null for an unknown node id. */
  nodeScreenPosition(nodeId: number): { x: number; y: number } | null;
  /** Render an assembled board: zone tints + the placed tiles as regions. */
  applyPlacements(placements: TilePlacementView[]): void;
  /** Set the severed paths (chasms) as canonical `"lo-hi"` edge keys: those
   * edges are dropped from the grid and drawn with broken-path "T" barriers. */
  setSeveredEdges(severedEdges: string[]): void;
  /** Draw a grey I-beam on each constructed Bridge path, given as canonical
   * `"lo-hi"` edge keys. */
  setBridges(bridges: string[]): void;
  /** Barracks aims: an arrow from each aimed Barracks to its target node — red
   * for attack, blue for defend. Pass the full list each time; `[]` clears. */
  setAims(aims: readonly AimView[]): void;
  /** Construct-bridge flow: highlight the eligible paths — `allowed` (tappable,
   * firing `onEdgeClick`) and `tooCostly` (in range but unaffordable, dim) — each
   * an `[a, b]` node-id pair. Clear everything with `null`. */
  setBridgeHighlight(
    allowed: ReadonlyArray<readonly [number, number]> | null,
    tooCostly?: ReadonlyArray<readonly [number, number]> | null,
  ): void;
  /** Tilt the board toward a 3D table view (true) or flat top-down (false);
   * the transition animates smoothly. */
  setTilt(enabled: boolean): void;
  /** Zoom the view about its centre by `factor` (>1 in, <1 out), clamped to the
   * viewport's zoom limits. */
  zoomByFactor(factor: number): void;
  /** Smoothly pan + zoom the board to centre node `nodeId` and briefly flash a
   * highlight ring on it (e.g. when a Turn History entry is clicked). No-op for
   * an unknown id. */
  focusNode(nodeId: number): void;
  /** Persistently ring-highlight a set of nodes (e.g. a player's fortress while
   * its roster detail popover is open). Pass `null` to clear. */
  highlightNodes(nodeIds: number[] | null): void;
  /** Smoothly pan + zoom the board to centre the centroid of `nodeIds` (e.g. the
   * "View on board" button on a fortress detail). No-op for an empty/unknown set. */
  focusNodes(nodeIds: number[]): void;
  /** Construct-building flow: pulse-highlight `buildable` nodes (in range +
   * affordable) as rings whose colour climbs with the node's zone Vigor penalty
   * — green (none) → pink (ridgeline) → orange (crag) → yellow (summit) — and
   * mark `tooCostly` nodes (in range but unaffordable) with a static grey "X".
   * `vigorPenalty` maps a node id to the Vigor it would cost to build there (only
   * nodes that pay one); it drives the ring colour and the "-N ⬡" popup shown
   * over a penalised node while the cursor hovers it. `oneTimeGain` maps a node
   * id to the one-time tile resource a build there would collect (zone bonus
   * included), shown as a "+X <resource>" in the same hover popup. Clear with
   * `null`. */
  setBuildHighlight(
    buildable: number[] | null,
    tooCostly?: number[] | null,
    vigorPenalty?: ReadonlyMap<number, number> | null,
    oneTimeGain?: ReadonlyMap<number, { resource: string; amount: number }> | null,
  ): void;
  /** Construct-building flow: mark the node the player has tentatively picked
   * (pending a Confirm), or clear the marker with `null`. Deliberately loud —
   * bold white double ring + expanding ping — so it stands apart from the many
   * pulsing candidate rings around it. */
  setBuildSelection(nodeId: number | null): void;
  /** Ghost-preview a SELECTED queued move on the board: a translucent building
   * (in the owner's colour, dashed ring) on the node a queued construct will
   * land on, or a translucent I-beam on a queued bridge's path. `null` clears —
   * the ghost shows only while its queue entry is selected in the panel. */
  setQueuedGhost(
    ghost:
      | { kind: "building"; nodeId: number; building: string; color: number }
      | { kind: "bridge"; from: number; to: number }
      | null,
  ): void;
  /** Construct-building flow: set the board cursor to a CSS value (e.g. the
   * selected building's glyph), or restore the default with `null`. */
  setBuildCursor(cursor: string | null): void;
  /** Setup phase: when active, pulse-highlight the free outer-tile nodes the
   * player may pick for their home base; when inactive, clear the highlight. */
  setHomePlacementActive(active: boolean): void;
  /** Setup phase: mark the node the player has tentatively picked (pending a
   * Confirm), or clear the marker with `null`. */
  setHomeSelection(nodeId: number | null): void;
  /** Whether `nodeId` is a legal home-base spot right now: a free node on the
   * outermost ("forest") tiles. Mirrors the server's check, for click gating. */
  isLegalHomeNode(nodeId: number): boolean;
  /** Map a client-coordinate point (e.g. `PointerEvent.clientX/Y`) to the
   * shared board plane — hex-size units from the board centre, the wire format
   * for cursor sync. Inverts this client's whole camera (pan/zoom/rotation/
   * tilt), so the same returned value means the same physical board spot on
   * every client. Null when the point is outside the board canvas. */
  boardPointFromClient(
    clientX: number,
    clientY: number,
  ): { x: number; y: number } | null;
  /** Replace the set of remote cursors shown on the board (same board-plane
   * units as `boardPointFromClient`). Call with each state patch; the layer
   * interpolates between samples itself, so ~10Hz input renders smoothly.
   * Cursors keep constant screen size and stay upright under any local
   * zoom/rotation/tilt. Pass `[]` to clear. */
  setRemoteCursors(cursors: readonly RemoteCursorView[]): void;
  /** Play a one-shot tap ripple at a board-plane point (same units as the
   * cursors) — the presence cue for touch players, who have no hover cursor
   * to stream: two soft expanding rings in the tapper's colour that fade out
   * over ~a second. Fire-and-forget; cleans itself up. */
  showRemoteTap(tap: { x: number; y: number; color: number }): void;
  /** Play an emoji reaction at a board-plane point: pulsates in (pop +
   * bounce), holds a beat, then fades out quickly while drifting up.
   * Fire-and-forget; cleans itself up. */
  showEmoji(reaction: { x: number; y: number; emoji: string }): void;
  destroy(): void;
}

type NodeView = NodeRenderState;

/** Lighten/darken a colour by a multiplicative factor, clamped per channel. */
function shade(base: number, factor: number): number {
  const r = (base >> 16) & 0xff;
  const g = (base >> 8) & 0xff;
  const b = base & 0xff;
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v * factor)));
  return (c(r) << 16) | (c(g) << 8) | c(b);
}

export async function createPixiGame(
  parent: HTMLElement,
  onNodeClick?: NodeClickHandler,
  onEdgeClick?: EdgeClickHandler,
  onNodeHover?: NodeHoverHandler,
): Promise<PixiGame> {
  // Pixel bounds of the generated nodes, shifted so the board starts at (0,0).
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of BOARD.nodes) {
    minX = Math.min(minX, node.pixel.x);
    minY = Math.min(minY, node.pixel.y);
    maxX = Math.max(maxX, node.pixel.x);
    maxY = Math.max(maxY, node.pixel.y);
  }
  const offsetX = PADDING - minX;
  const offsetY = PADDING - minY;
  const worldWidth = maxX - minX + PADDING * 2;
  const worldHeight = maxY - minY + PADDING * 2;
  const px = (x: number) => x + offsetX;
  const py = (y: number) => y + offsetY;

  const app = new Application();
  await app.init({
    background: BG_COLOR,
    resizeTo: parent,
    antialias: true,
    autoDensity: true,
    resolution: window.devicePixelRatio || 1,
  });
  parent.appendChild(app.canvas);

  // Pre-load the resource icon art so the first node render already has textures.
  await loadResourceTextures();

  const viewport = new Viewport({
    screenWidth: app.screen.width,
    screenHeight: app.screen.height,
    worldWidth,
    worldHeight,
    events: app.renderer.events,
  });
  app.stage.addChild(viewport);

  viewport
    // Left button only for panning, so the middle button is free to rotate.
    .drag({ mouseButtons: "left" })
    .pinch() // two-finger pinch to zoom
    .wheel()
    .decelerate({ friction: 0.92 })
    .clampZoom({ minScale: MIN_ZOOM, maxScale: MAX_ZOOM });
  viewport.moveCenter(worldWidth / 2, worldHeight / 2);

  const board = new Container();
  viewport.addChild(board);
  // Anchor the board on its centre so the 3D tilt squashes in place rather than
  // sliding the board up the screen.
  board.pivot.set(worldWidth / 2, worldHeight / 2);
  board.position.set(worldWidth / 2, worldHeight / 2);

  // Rotation lives on an inner container pivoted at the same centre, so it spins
  // the board in its own plane *underneath* the outer board's vertical tilt
  // squash (rotate-then-tilt, like a map's bearing + pitch). All board content
  // goes here; the gesture handlers below drive `rotator.rotation`.
  const rotator = new Container();
  rotator.pivot.set(worldWidth / 2, worldHeight / 2);
  rotator.position.set(worldWidth / 2, worldHeight / 2);
  board.addChild(rotator);

  // Exact physical footprint of each tile: the same hex-centre triangles used
  // by applyPlacements() below. These points let the camera frame a tile (or the
  // complete board) correctly even after the board has been rotated or tilted.
  const tileFocusPoints = new Map<string, Pt[]>(
    SLOTS.map((slot) => [
      slot.id,
      slot.nodeIds.flatMap((id) => {
        const node = BOARD.nodes[id];
        if (!node) return [];
        return node.hexes.map((h) => {
          const p = hexToPixel(LAYOUT, h);
          return { x: px(p.x), y: py(p.y) };
        });
      }),
    ]),
  );
  const boardFocusPoints = [...tileFocusPoints.values()].flat();

  const animateToFit = (
    points: readonly Pt[],
    padding: number,
    preserveScale = false,
  ): boolean => {
    if (points.length === 0) return false;
    let bx0 = Infinity;
    let by0 = Infinity;
    let bx1 = -Infinity;
    let by1 = -Infinity;
    for (const point of points) {
      // Cancel the viewport's current camera transform while retaining the
      // rotator + board transforms, yielding stable viewport-world bounds.
      const world = viewport.toLocal(rotator.toGlobal(point));
      bx0 = Math.min(bx0, world.x);
      by0 = Math.min(by0, world.y);
      bx1 = Math.max(bx1, world.x);
      by1 = Math.max(by1, world.y);
    }
    const width = Math.max(1, bx1 - bx0);
    const height = Math.max(1, by1 - by0);
    const availableWidth = Math.max(1, viewport.screenWidth - padding * 2);
    const availableHeight = Math.max(1, viewport.screenHeight - padding * 2);
    const scale = preserveScale
      ? viewport.scaled
      : Math.max(
          MIN_ZOOM,
          Math.min(MAX_ZOOM, availableWidth / width, availableHeight / height),
        );
    viewport.animate({
      time: 600,
      position: { x: (bx0 + bx1) / 2, y: (by0 + by1) / 2 },
      scale,
      ease: "easeInOutSine",
      removeOnInterrupt: true,
    });
    return true;
  };

  let selectedTileId: string | null = null;
  let lastTileTap: { slotId: string; at: number } | null = null;
  const clearTileSelection = () => {
    selectedTileId = null;
    lastTileTap = null;
  };
  const onTileTap = (slotId: string) => {
    const now = performance.now();
    const doubleTap =
      lastTileTap?.slotId === slotId && now - lastTileTap.at <= DOUBLE_TAP_MS;
    lastTileTap = doubleTap ? null : { slotId, at: now };
    if (!doubleTap) return;

    // Double-tapping the focused tile toggles back to the complete-board view.
    // Double-tapping a different tile keeps the focused scale and pans straight
    // to that tile instead.
    if (selectedTileId === slotId) {
      clearTileSelection();
      animateToFit(boardFocusPoints, BOARD_FOCUS_PADDING);
      return;
    }
    const points = tileFocusPoints.get(slotId);
    if (
      points &&
      animateToFit(points, TILE_FOCUS_PADDING, selectedTileId !== null)
    ) {
      selectedTileId = slotId;
    }
  };

  // Once the player manually changes the camera, it no longer represents a
  // selected tile; their next double-tap should focus rather than zoom out.
  viewport.on("drag-start", clearTileSelection);
  viewport.on("pinch-start", clearTileSelection);
  viewport.on("wheel-start", clearTileSelection);

  // Tilt state: `tilt` eases toward `tiltTarget` (0 = flat 2D, 1 = 3D), applied
  // each frame as a vertical squash of the board.
  let tilt = 0;
  let tiltTarget = 0;
  app.ticker.add(() => {
    if (Math.abs(tilt - tiltTarget) < 0.001) {
      if (tilt !== tiltTarget) {
        tilt = tiltTarget;
        board.scale.y = 1 - tilt * (1 - TILT_MIN_SCALE_Y);
      }
      return;
    }
    tilt += (tiltTarget - tilt) * TILT_LERP;
    board.scale.y = 1 - tilt * (1 - TILT_MIN_SCALE_Y);
  });

  // Tile regions are the bottom layer (the filled board).
  const regions = new Container();
  rotator.addChild(regions);

  // The hex lattice is drawn as faint lines OVER the tiles (and bare areas), so
  // each tile shows its internal grid like the printed board. Node dots are
  // added later, on top of everything. Edges incident to a severed
  // (disconnected) node are skipped — that path is gone, leaving only its "T".
  // Severed paths (chasms): canonical "lo-hi" edge keys. A severed edge is
  // dropped from the grid and drawn instead as a broken-path "T" barrier at each
  // end (matching the tile library), leaving its two nodes visibly cut apart.
  const severedEdges = new Set<string>();
  const edges = new Graphics();
  const redrawEdges = () => {
    edges.clear();
    for (const [a, b] of BOARD.edges) {
      if (severedEdges.has(edgeKey(a, b))) continue;
      const na = BOARD.nodes[a];
      const nb = BOARD.nodes[b];
      if (!na || !nb) continue;
      edges.moveTo(px(na.pixel.x), py(na.pixel.y));
      edges.lineTo(px(nb.pixel.x), py(nb.pixel.y));
    }
    edges.stroke({ color: GRID_COLOR, width: 1, alpha: 0.18 });
  };
  redrawEdges();
  rotator.addChild(edges);

  // "T" barrier near `from`, pointing along the edge toward `to` (board coords).
  const pushBarrier = (
    g: Graphics,
    from: { x: number; y: number },
    to: { x: number; y: number },
  ) => {
    const fx = px(from.x);
    const fy = py(from.y);
    const dx = px(to.x) - fx;
    const dy = py(to.y) - fy;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy;
    const ny = ux;
    const s0 = NODE_RADIUS + 1.5; // stem starts just outside the dot
    const cap = NODE_RADIUS + 7.5; // crossbar a short, fixed step out
    const half = 2.7; // crossbar half-length
    g.moveTo(fx + ux * s0, fy + uy * s0).lineTo(fx + ux * cap, fy + uy * cap);
    g.moveTo(fx + ux * cap + nx * half, fy + uy * cap + ny * half).lineTo(
      fx + ux * cap - nx * half,
      fy + uy * cap - ny * half,
    );
  };
  const severedGfx = new Graphics();
  const redrawSevered = () => {
    severedGfx.clear();
    for (const key of severedEdges) {
      const dash = key.indexOf("-");
      const na = BOARD.nodes[Number(key.slice(0, dash))];
      const nb = BOARD.nodes[Number(key.slice(dash + 1))];
      if (!na || !nb) continue;
      pushBarrier(severedGfx, na.pixel, nb.pixel);
      pushBarrier(severedGfx, nb.pixel, na.pixel);
    }
    severedGfx.stroke({ color: TILE_BORDER, width: 1.2, cap: "round" });
  };
  rotator.addChild(severedGfx);

  // Constructed Bridges: a grey I-beam drawn along each bridged path — a web
  // running the length of the edge with a perpendicular flange at each end. Sits
  // above the grid + severed barriers but below the node tokens (added before the
  // node loop), so a building on an endpoint covers the beam's near end.
  const bridgeEdges = new Set<string>();
  const drawIBeam = (
    g: Graphics,
    from: { x: number; y: number },
    to: { x: number; y: number },
  ) => {
    const seg = iBeamSegments(
      { x: px(from.x), y: py(from.y) },
      { x: px(to.x), y: py(to.y) },
    );
    g.moveTo(seg.web[0].x, seg.web[0].y).lineTo(seg.web[1].x, seg.web[1].y);
    for (const [p, q] of seg.flanges) g.moveTo(p.x, p.y).lineTo(q.x, q.y);
  };
  const bridgeGfx = new Graphics();
  const redrawBridges = () => {
    bridgeGfx.clear();
    const beams = () => {
      for (const key of bridgeEdges) {
        const dash = key.indexOf("-");
        const na = BOARD.nodes[Number(key.slice(0, dash))];
        const nb = BOARD.nodes[Number(key.slice(dash + 1))];
        if (!na || !nb) continue;
        drawIBeam(bridgeGfx, na.pixel, nb.pixel);
      }
    };
    // A black outline pass then a dark-grey (almost black) fill pass, so the slim
    // beam still reads on any tile. Widths derive from the shared I-beam metrics
    // and the board's fixed edge length (HEX_SIZE), so they match the cards.
    beams();
    bridgeGfx.stroke({
      color: BRIDGE_OUTLINE_COLOR,
      width: I_BEAM.outlineWidth * HEX_SIZE,
      cap: "round",
    });
    beams();
    bridgeGfx.stroke({
      color: BRIDGE_FILL_COLOR,
      width: I_BEAM.fillWidth * HEX_SIZE,
      cap: "round",
    });
  };
  rotator.addChild(bridgeGfx);

  // Barracks aims: a straight arrow from each aimed Barracks to its target node
  // (red attack / blue defend), trimmed clear of both end tokens, with a small
  // head at the target end. Above bridges, below the node tokens — the posture
  // reads at a glance without hiding any building.
  let aims: readonly AimView[] = [];
  const aimGfx = new Graphics();
  const redrawAims = () => {
    aimGfx.clear();
    for (const aim of aims) {
      const na = BOARD.nodes[aim.from];
      const nb = BOARD.nodes[aim.to];
      if (!na || !nb) continue;
      const ax = px(na.pixel.x);
      const ay = py(na.pixel.y);
      const bx = px(nb.pixel.x);
      const by = py(nb.pixel.y);
      const dx = bx - ax;
      const dy = by - ay;
      const len = Math.hypot(dx, dy) || 1;
      const ux = dx / len;
      const uy = dy / len;
      // Shaft endpoints, trimmed just outside each node's token/dot.
      const s0 = BUILD_R + 1.5;
      const s1 = BUILD_R + 2.5;
      const sx = ax + ux * s0;
      const sy = ay + uy * s0;
      const tx = bx - ux * s1;
      const ty = by - uy * s1;
      if ((len - s0 - s1) <= 2) continue; // degenerate: adjacent-ish overlap
      const head = 5; // arrowhead arm length along the shaft
      const path = () => {
        aimGfx.moveTo(sx, sy).lineTo(tx, ty);
        aimGfx
          .moveTo(tx - ux * head - uy * head * 0.6, ty - uy * head + ux * head * 0.6)
          .lineTo(tx, ty)
          .lineTo(tx - ux * head + uy * head * 0.6, ty - uy * head - ux * head * 0.6);
      };
      const color = aim.mode === "attack" ? AIM_ATTACK_COLOR : AIM_DEFEND_COLOR;
      path();
      aimGfx.stroke({ color: AIM_OUTLINE_COLOR, width: 3.8, alpha: 0.5, cap: "round", join: "round" });
      path();
      aimGfx.stroke({ color, width: 2, cap: "round", join: "round" });
    }
  };
  rotator.addChild(aimGfx);

  // Setup-phase home-base highlight: pulsing rings over the free outer-tile
  // nodes a player may pick. Drawn under the node dots so they stay clickable.
  const homeLayer = new Graphics();
  homeLayer.visible = false;
  rotator.addChild(homeLayer);
  let homePulse: gsap.core.Tween | null = null;

  // The tentatively-picked home node (pending Confirm), drawn distinctly over
  // the candidate highlight.
  const homeSelectLayer = new Graphics();
  homeSelectLayer.visible = false;
  rotator.addChild(homeSelectLayer);

  // Transient highlight ring drawn over a node `focusNode` pans to (e.g. from a
  // Turn History click), pulsed then faded by gsap.
  const focusGfx = new Graphics();
  focusGfx.visible = false;
  rotator.addChild(focusGfx);
  let focusTween: gsap.core.Tween | null = null;
  let focusFade: gsap.core.Tween | null = null;

  // Construct-building flow. Two layers, drawn under the node dots so they stay
  // clickable: a static grey ring for in-range-but-unaffordable nodes, and a
  // pulsing green ring for the legal (in-range + affordable) build spots.
  const buildDimLayer = new Graphics();
  buildDimLayer.visible = false;
  rotator.addChild(buildDimLayer);
  const buildLayer = new Graphics();
  buildLayer.visible = false;
  rotator.addChild(buildLayer);
  let buildPulse: gsap.core.Tween | null = null;

  // The tentatively-picked build node (pending Confirm), drawn distinctly over
  // the legal-target rings — the build-flow twin of `homeSelectLayer`.
  const buildSelectLayer = new Graphics();
  buildSelectLayer.visible = false;
  rotator.addChild(buildSelectLayer);
  // Expanding "radar ping" ring radiating from the picked node — animation
  // (not just colour/size) is what separates it from the sea of candidate
  // rings, which only pulse in place. Positioned at the node and scaled about
  // its own origin.
  const buildSelectPing = new Graphics();
  buildSelectPing.visible = false;
  rotator.addChild(buildSelectPing);
  let buildSelectPulse: gsap.core.Tween | null = null;

  // Ghost preview of a SELECTED queued move (translucent building / bridge
  // I-beam). Whole-layer alpha keeps every stroke uniformly see-through.
  const queuedGhostLayer = new Graphics();
  queuedGhostLayer.visible = false;
  queuedGhostLayer.alpha = 0.55;
  rotator.addChild(queuedGhostLayer);

  // Construct-bridge flow: highlight the eligible paths (edges) and make them
  // tappable. One interactive Graphics per eligible edge lives in this container
  // (passive, so only its children are hit-tested). Added before the node loop so
  // node taps still win near a node; an edge tap fires `onEdgeClick`.
  const bridgeHints = new Container();
  bridgeHints.eventMode = "passive";
  bridgeHints.visible = false;
  rotator.addChild(bridgeHints);
  let bridgePulse: gsap.core.Tween | null = null;
  // Distance from point (x,y) to the segment a→b, for the per-edge tap hitArea.
  const segDist = (
    x: number,
    y: number,
    ax: number,
    ay: number,
    bx: number,
    by: number,
  ): number => {
    const dx = bx - ax;
    const dy = by - ay;
    const l2 = dx * dx + dy * dy;
    if (l2 === 0) return Math.hypot(x - ax, y - ay);
    let t = ((x - ax) * dx + (y - ay) * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(x - (ax + t * dx), y - (ay + t * dy));
  };

  // Persistent fortress highlight: gold rings pulsed around the nodes of the
  // fortress whose detail popover is currently open (driven from the roster).
  const fortressLayer = new Graphics();
  fortressLayer.visible = false;
  rotator.addChild(fortressLayer);
  let fortressPulse: gsap.core.Tween | null = null;

  // The active center→outward reveal animation (ticker-driven), if any.
  let revealTick: ((ticker: { deltaMS: number }) => void) | null = null;

  // All gsap tweens are tracked here so destroy() can kill any still in flight:
  // Pixi v8 nulls an object's internals on destroy, and a live tween would then
  // write `.scale`/`.alpha` onto freed objects. (We track manually rather than
  // use `gsap.context()`, which returns undefined in this gsap build — calling
  // `.add` on it threw inside setNodeState and broke Colyseus state sync.)
  const liveTweens = new Set<gsap.core.Tween>();
  const track = (tween: gsap.core.Tween): gsap.core.Tween => {
    liveTweens.add(tween);
    tween.eventCallback("onComplete", () => liveTweens.delete(tween));
    return tween;
  };

  const nodeViews: NodeView[] = new Array<NodeView>(BOARD.nodes.length);
  const nodeGfx: Graphics[] = new Array<Graphics>(BOARD.nodes.length);

  function renderNode(id: number): void {
    const g = nodeGfx[id];
    const v = nodeViews[id];
    if (!g || !v) return;
    g.clear();
    if (v.ownerColor !== null) {
      // A built node: a faceted owner-coloured token, shaped by building kind
      // (scout circle / barracks triangle / mine square / home base house) so it
      // pops off the board and never reads as one of the flat resource icons.
      // If a resource sits under the building, draw its (flat) icon first, tucked
      // toward a hex cell so it peeks out from under the token, which is drawn on
      // top and covers a little of it.
      if (v.resource) {
        const d = NODE_RES_DIR[id]!;
        drawResourceIcon(
          g,
          v.resource,
          v.permanence === "oneTime",
          ICON_R * 0.85,
          d.x * RES_UNDER_OFFSET,
          d.y * RES_UNDER_OFFSET,
        );
      }
      drawBuilding(g, v.isHomeBase ? "house" : v.building, v.ownerColor);
    } else {
      // Always draw the movement dot; a printed-resource token sits on top.
      // One-time tokens are transparent, so the dot still shows through them.
      g.circle(0, 0, NODE_RADIUS).fill(NODE_COLOR);
      if (v.resource) {
        drawResourceIcon(g, v.resource, v.permanence === "oneTime");
      }
      // An empty node occupied by a Fortress gets its "X" mark (as on the card
      // art), in the fortress owner's colour over a dark outline pass. Nodes
      // with a building keep their token instead (the branch above).
      if (v.fortressColor !== null) {
        const d = 4.4;
        const arms = () =>
          g.moveTo(-d, -d).lineTo(d, d).moveTo(d, -d).lineTo(-d, d);
        arms();
        g.stroke({ color: 0x0e0e11, width: 4, cap: "round" });
        arms();
        g.stroke({ color: v.fortressColor, width: 2.2, cap: "round" });
      }
    }
    // A node's chasms are shown only by the per-edge "T" barriers (severed
    // layer); the node itself gets no extra marker. `v.disconnected` still
    // drives game logic (bridgeable), just nothing drawn on the dot.
  }

  // Distinguish a clean tap (place/select) from a drag-to-pan: track how far the
  // pointer travels while down, in screen space (set by the canvas pointer
  // handlers below). A press that wanders past TAP_TOL is a drag, so the node
  // `pointertap` is suppressed — a build is only placed on a real tap/click.
  const TAP_TOL = 6; // CSS px of travel that turns a tap into a drag
  let tapDownPt: { x: number; y: number } | null = null;
  let tapDragged = false;

  for (const node of BOARD.nodes) {
    nodeViews[node.id] = {
      ownerColor: null,
      building: "",
      resource: "",
      permanence: "",
      disconnected: false,
      isHomeBase: false,
      fortressColor: null,
    };
    const g = new Graphics();
    g.position.set(px(node.pixel.x), py(node.pixel.y));
    g.eventMode = "static";
    g.cursor = "pointer";
    g.hitArea = {
      contains: (x, y) => x * x + y * y <= HIT_RADIUS * HIT_RADIUS,
    };
    g.on("pointertap", () => {
      if (!tapDragged) {
        onNodeClick?.(node.id);
        const slotId = SLOT_OF_NODE.get(node.id);
        if (slotId) onTileTap(slotId);
      }
    });
    // Hover in/out (mouse only — Pixi doesn't synthesise these for touch).
    // Drives the board fortress detail popover in the App layer, plus the
    // build-flow Vigor-penalty popup below.
    g.on("pointerover", () => {
      onNodeHover?.(node.id);
      showVigorPopup(node.id);
    });
    g.on("pointerout", () => {
      onNodeHover?.(null);
      hideVigorPopup();
    });
    rotator.addChild(g);
    nodeGfx[node.id] = g;
    renderNode(node.id);
  }

  // Build-flow hover popup: a "-N ⬡" Vigor-penalty and/or "+X <resource>"
  // one-time-pickup label shown over a candidate node only while the cursor
  // hovers it. Added last so it sits above the node dots. `buildVigorPenalty`
  // and `buildOneTimeGain` (set by setBuildHighlight) drive it.
  const buildHoverLabel = new Container();
  buildHoverLabel.visible = false;
  rotator.addChild(buildHoverLabel);
  let buildVigorPenalty: ReadonlyMap<number, number> | null = null;
  let buildOneTimeGain: ReadonlyMap<
    number,
    { resource: string; amount: number }
  > | null = null;

  const hideVigorPopup = () => {
    buildHoverLabel.visible = false;
    buildHoverLabel
      .removeChildren()
      .forEach((c) => c.destroy({ children: true }));
  };
  const showVigorPopup = (nodeId: number) => {
    hideVigorPopup();
    const pen = buildVigorPenalty?.get(nodeId) ?? 0;
    const gain = buildOneTimeGain?.get(nodeId);
    const node = BOARD.nodes[nodeId];
    if ((pen <= 0 && !gain) || !node) return;
    const iconR = 6.5;
    const gap = 3; // between a number and its icon
    const groupGap = 8; // between the "-N ⬡" and "+X <resource>" groups
    // Up to two "<number> <icon>" groups on one row: the Vigor cost of building
    // here, and the one-time tile pickup the build would collect.
    const groups: { text: Text; icon: Graphics }[] = [];
    const makeGroup = (label: string, kind: string, oneTime: boolean) => {
      const text = new Text({
        text: label,
        style: {
          fontFamily: "system-ui, sans-serif",
          fontSize: 17,
          fontWeight: "700",
          fill: 0x2b2620,
          stroke: { color: 0xffffff, width: 3 },
        },
      });
      text.anchor.set(0, 0.5);
      const icon = new Graphics();
      drawResourceIcon(icon, kind, oneTime, iconR);
      groups.push({ text, icon });
    };
    if (pen > 0) makeGroup(`-${pen}`, "vigor", false);
    // One-time icon variant, matching the node's printed marker being collected.
    if (gain) makeGroup(`+${gain.amount}`, gain.resource, true);
    // Lay the row out centred a little above the node (clear of the cursor
    // and the candidate ring).
    const groupW = (g: { text: Text }) => g.text.width + gap + iconR * 2;
    const totalW =
      groups.reduce((w, g) => w + groupW(g), 0) +
      (groups.length - 1) * groupGap;
    const yOff = -(HIT_RADIUS + 11);
    let x = -totalW / 2;
    for (const g of groups) {
      g.text.position.set(x, yOff);
      g.icon.position.set(x + g.text.width + gap + iconR, yOff);
      buildHoverLabel.addChild(g.text, g.icon);
      x += groupW(g) + groupGap;
    }
    buildHoverLabel.position.set(px(node.pixel.x), py(node.pixel.y));
    // Counter-spin so the label reads upright whatever the board's rotation.
    buildHoverLabel.rotation = -rotator.rotation;
    buildHoverLabel.visible = true;
  };

  // --- Remote cursors: other players' live pointers on the board -----------
  // Positions arrive in the shared board plane (hex units from the centre), so
  // parenting the layer under `rotator` makes THIS client's camera re-project
  // them for free — every client sees each pointer on the same physical spot
  // of the same tile whatever their own pan/zoom/rotation/tilt. Topmost layer,
  // and never interactive (a cursor must not eat board taps).
  const cursorLayer = new Container();
  cursorLayer.eventMode = "none";
  rotator.addChild(cursorLayer);

  interface CursorEntry {
    root: Container;
    color: number;
    name: string;
    /** Received samples (local receive time), interpolated by the ticker. */
    samples: { t: number; x: number; y: number }[];
    /** Drawn position + velocity: the buffered LERP chased by a critically
     * damped spring (CURSOR_SPRING_OMEGA), so sample kinks and arrival jitter
     * never show. */
    rx: number;
    ry: number;
    vx: number;
    vy: number;
  }
  const remoteCursors = new Map<string, CursorEntry>();

  // A classic pointer arrow with its tip at (0,0) — the hotspot — in the
  // player's colour over a dark halo (reads on any tile), plus a name pill.
  const makeCursorSprite = (color: number, name: string): Container => {
    const root = new Container();
    const arrowPts = [0, 0, 0, 14, 3.6, 10.8, 6.2, 16.2, 8.6, 15, 6, 9.8, 10.6, 9.6];
    const arrow = new Graphics();
    arrow.poly(arrowPts).stroke({ color: 0x14110d, width: 3.6, join: "round" });
    arrow.poly(arrowPts).fill(color);
    arrow.poly(arrowPts).stroke({ color: 0xffffff, width: 1.1, join: "round" });
    root.addChild(arrow);

    const text = new Text({
      text: name,
      style: {
        fontFamily: "system-ui, sans-serif",
        fontSize: 11,
        fontWeight: "700",
        fill: 0x14110d,
      },
    });
    const padX = 5;
    const padY = 2.5;
    const pill = new Graphics();
    pill
      .roundRect(0, 0, text.width + padX * 2, text.height + padY * 2, 6)
      .fill({ color, alpha: 0.92 })
      .stroke({ color: 0xffffff, width: 1, alpha: 0.85 });
    pill.position.set(9, 15);
    text.position.set(9 + padX, 15 + padY);
    root.addChild(pill, text);
    return root;
  };

  /** Buffer position at time `t`: LERP between the bracketing samples, clamped
   * to the endpoints (idle cursor holds its last sample; a brand-new one snaps). */
  const cursorSampleAt = (
    samples: CursorEntry["samples"],
    t: number,
  ): { x: number; y: number } | null => {
    const last = samples[samples.length - 1];
    if (!last) return null;
    if (t >= last.t) return last;
    for (let i = samples.length - 2; i >= 0; i--) {
      const a = samples[i]!;
      if (t >= a.t) {
        const b = samples[i + 1]!;
        const f = (t - a.t) / (b.t - a.t || 1);
        return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
      }
    }
    return samples[0]!;
  };

  app.ticker.add((ticker: { deltaMS: number }) => {
    if (remoteCursors.size === 0) return;
    const renderT = performance.now() - CURSOR_INTERP_DELAY_MS;
    const snap = ticker.deltaMS > CURSOR_SNAP_DELTA_MS;
    // Semi-implicit Euler; capped dt keeps the spring stable (< 2/omega).
    const dt = Math.min(ticker.deltaMS, 50) / 1000;
    const w = CURSOR_SPRING_OMEGA;
    // Constant screen size: cancel the local zoom. Upright + unsquashed: the
    // child rotation cancels `rotator`'s spin, and the y counter-scale cancels
    // the tilt squash `board` applies above it.
    const s = Math.max(
      CURSOR_MIN_SCALE,
      Math.min(CURSOR_MAX_SCALE, 1 / (viewport.scaled || 1)),
    );
    for (const entry of remoteCursors.values()) {
      const p = cursorSampleAt(entry.samples, renderT);
      if (!p) continue;
      if (snap) {
        entry.rx = p.x;
        entry.ry = p.y;
        entry.vx = 0;
        entry.vy = 0;
      } else {
        // Critically damped spring toward the buffered point.
        entry.vx += (w * w * (p.x - entry.rx) - 2 * w * entry.vx) * dt;
        entry.vy += (w * w * (p.y - entry.ry) - 2 * w * entry.vy) * dt;
        entry.rx += entry.vx * dt;
        entry.ry += entry.vy * dt;
      }
      entry.root.position.set(px(entry.rx * HEX_SIZE), py(entry.ry * HEX_SIZE));
      entry.root.rotation = -rotator.rotation;
      entry.root.scale.set(s, s / (board.scale.y || 1));
    }
  });

  const handleResize = () => {
    viewport.resize(app.screen.width, app.screen.height, worldWidth, worldHeight);
  };
  window.addEventListener("resize", handleResize);

  // --- Rotation gestures: middle-drag (mouse) + two-finger twist (touch) ----
  // Pinch-to-zoom is handled by the viewport's `.pinch()` plugin; these handlers
  // only add rotation, applied to `rotator` so it composes under the tilt squash.
  const canvas = app.canvas;
  canvas.style.touchAction = "none"; // we own all gestures; no browser panning

  const canvasPoint = (e: PointerEvent) => {
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  // Shortest signed delta between two angles, into (-π, π].
  const angleDelta = (to: number, from: number) => {
    let d = to - from;
    while (d > Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    return d;
  };

  // Angles are measured in the board's *unsquashed* plane: the tilt squashes
  // screen-Y by `board.scale.y`, so undo it (divide Δy) before atan2, and the
  // rotation then tracks the cursor/fingers 1:1 even when the board is tilted.
  // Middle-mouse drag: twist around the board's on-screen centre.
  let midRotating = false;
  let lastMidAngle = 0;
  const angleAboutBoardCentre = (p: { x: number; y: number }) => {
    const c = viewport.toScreen(worldWidth / 2, worldHeight / 2);
    return Math.atan2((p.y - c.y) / (board.scale.y || 1), p.x - c.x);
  };

  // Two-finger twist: rotate by the change in the angle between the two fingers.
  // (A common centre cancels in the Δ, so only the squash factor matters here.)
  const touches = new Map<number, { x: number; y: number }>();
  let twoFingerAngle: number | null = null;
  const fingerAngle = (): number | null => {
    if (touches.size !== 2) return null;
    const [a, b] = [...touches.values()];
    return Math.atan2((b!.y - a!.y) / (board.scale.y || 1), b!.x - a!.x);
  };

  const onPointerDown = (e: PointerEvent) => {
    tapDownPt = canvasPoint(e);
    tapDragged = false;
    if (e.pointerType === "touch") {
      touches.set(e.pointerId, canvasPoint(e));
      twoFingerAngle = fingerAngle();
    } else if (e.button === 1) {
      midRotating = true;
      lastMidAngle = angleAboutBoardCentre(canvasPoint(e));
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* capture is best-effort */
      }
      e.preventDefault();
    }
  };
  const onPointerMove = (e: PointerEvent) => {
    if (tapDownPt) {
      const p = canvasPoint(e);
      if (Math.hypot(p.x - tapDownPt.x, p.y - tapDownPt.y) > TAP_TOL)
        tapDragged = true;
    }
    if (e.pointerType === "touch") {
      if (!touches.has(e.pointerId)) return;
      touches.set(e.pointerId, canvasPoint(e));
      const a = fingerAngle();
      if (a !== null && twoFingerAngle !== null) {
        rotator.rotation += angleDelta(a, twoFingerAngle);
      }
      twoFingerAngle = a;
    } else if (midRotating) {
      // Self-correct if the middle button was released without a pointerup/
      // pointercancel reaching us (capture lost on blur, OS gesture steal, …),
      // so we never rotate on a plain button-less move.
      if ((e.buttons & 4) === 0) {
        midRotating = false;
        return;
      }
      const a = angleAboutBoardCentre(canvasPoint(e));
      rotator.rotation += angleDelta(a, lastMidAngle);
      lastMidAngle = a;
    }
  };
  const onPointerEnd = (e: PointerEvent) => {
    tapDownPt = null; // stop tracking; `tapDragged` persists for the pointertap
    if (e.pointerType === "touch") {
      touches.delete(e.pointerId);
      twoFingerAngle = fingerAngle(); // null until two fingers are down again
    } else if (midRotating) {
      midRotating = false;
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* may already be released */
      }
    }
  };
  // Suppress the browser's middle-click autoscroll so dragging it rotates.
  const onMouseDown = (e: MouseEvent) => {
    if (e.button === 1) e.preventDefault();
  };
  // If the browser drops the capture (blur, OS gesture), stop rotating at once.
  const onLostCapture = () => {
    midRotating = false;
  };

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerEnd);
  canvas.addEventListener("pointercancel", onPointerEnd);
  canvas.addEventListener("mousedown", onMouseDown);
  canvas.addEventListener("lostpointercapture", onLostCapture);

  return {
    app,
    viewport,
    setTilt(enabled) {
      tiltTarget = enabled ? 1 : 0;
    },
    zoomByFactor(factor) {
      clearTileSelection();
      const next = Math.max(
        MIN_ZOOM,
        Math.min(MAX_ZOOM, viewport.scaled * factor),
      );
      // `center: true` keeps the viewport centre fixed while zooming.
      viewport.setZoom(next, true);
    },
    focusNode(nodeId) {
      const node = BOARD.nodes[nodeId];
      if (!node) return;
      clearTileSelection();
      const lx = px(node.pixel.x);
      const ly = py(node.pixel.y);
      // The node lives under `rotator` (which may be rotated/tilted), so map its
      // local point to viewport-world coords via the stage so the centre is
      // correct at any orientation/zoom.
      const world = viewport.toLocal(rotator.toGlobal({ x: lx, y: ly }));
      // Zoom in to at least a comfortable level, never out, clamped to the max.
      const targetScale = Math.min(MAX_ZOOM, Math.max(viewport.scaled, 2));
      viewport.animate({
        time: 600,
        position: world,
        scale: targetScale,
        ease: "easeInOutSine",
        removeOnInterrupt: true,
      });

      // Flash a highlight ring on the node so it's easy to spot after the pan.
      focusTween?.kill();
      focusFade?.kill();
      focusGfx.clear();
      focusGfx.position.set(lx, ly);
      focusGfx.circle(0, 0, 11).stroke({ color: 0xffd65d, width: 2.2 });
      focusGfx.visible = true;
      focusGfx.alpha = 1;
      focusGfx.scale.set(1);
      focusTween = track(
        gsap.fromTo(
          focusGfx.scale,
          { x: 0.5, y: 0.5 },
          { x: 1.7, y: 1.7, duration: 0.7, ease: "sine.out", repeat: 3, yoyo: true },
        ),
      );
      focusFade = track(
        gsap.to(focusGfx, {
          alpha: 0,
          duration: 2.6,
          ease: "sine.in",
          onComplete: () => {
            focusGfx.visible = false;
          },
        }),
      );
    },
    highlightNodes(nodeIds) {
      if (fortressPulse) {
        fortressPulse.kill();
        fortressPulse = null;
      }
      fortressLayer.clear();
      const ids = nodeIds ?? [];
      for (const id of ids) {
        const node = BOARD.nodes[id];
        if (node) fortressLayer.circle(px(node.pixel.x), py(node.pixel.y), 9);
      }
      fortressLayer.visible = ids.length > 0;
      if (ids.length > 0) {
        fortressLayer.stroke({ color: 0xffd65d, width: 2.4, alpha: 0.95 });
        fortressLayer.alpha = 0.95;
        fortressPulse = track(
          gsap.to(fortressLayer, {
            alpha: 0.4,
            duration: 0.85,
            yoyo: true,
            repeat: -1,
            ease: "sine.inOut",
          }),
        );
      }
    },
    focusNodes(nodeIds) {
      const nodes = nodeIds
        .map((id) => BOARD.nodes[id])
        .filter((n): n is (typeof BOARD.nodes)[number] => !!n);
      if (nodes.length === 0) return;
      clearTileSelection();
      const cx =
        nodes.reduce((s, n) => s + px(n.pixel.x), 0) / nodes.length;
      const cy =
        nodes.reduce((s, n) => s + py(n.pixel.y), 0) / nodes.length;
      const world = viewport.toLocal(rotator.toGlobal({ x: cx, y: cy }));
      const targetScale = Math.min(MAX_ZOOM, Math.max(viewport.scaled, 2));
      viewport.animate({
        time: 600,
        position: world,
        scale: targetScale,
        ease: "easeInOutSine",
        removeOnInterrupt: true,
      });
    },
    setBuildHighlight(buildable, tooCostly, vigorPenalty, oneTimeGain) {
      if (buildPulse) {
        buildPulse.kill();
        buildPulse = null;
      }
      buildLayer.clear();
      buildDimLayer.clear();
      buildVigorPenalty = vigorPenalty ?? null;
      buildOneTimeGain = oneTimeGain ?? null;
      hideVigorPopup();
      const greens = buildable ?? [];
      const greys = tooCostly ?? [];

      // In range but unaffordable: a static grey "X" (reachable, but you lack the
      // resources to build here), drawn instead of a ring.
      const xr = 5; // X arm half-length
      for (const id of greys) {
        const node = BOARD.nodes[id];
        if (!node) continue;
        const cx = px(node.pixel.x);
        const cy = py(node.pixel.y);
        buildDimLayer.moveTo(cx - xr, cy - xr).lineTo(cx + xr, cy + xr);
        buildDimLayer.moveTo(cx - xr, cy + xr).lineTo(cx + xr, cy - xr);
      }
      if (greys.length > 0) {
        buildDimLayer.stroke({
          color: 0x9a948c,
          width: 1.6,
          alpha: 0.7,
          cap: "round",
        });
      }
      buildDimLayer.visible = greys.length > 0;

      // Legal build spots, pulsing. Ring colour encodes the node's zone Vigor
      // penalty, climbing with the cost: green (none) → pink (ridgeline) →
      // orange (crag) → yellow (summit). Grouped so each colour is one stroke pass.
      const ringColor = (pen: number): number => {
        if (pen >= 5) return 0xffe23d; // summit → yellow
        if (pen >= 3) return 0xff9d3c; // crag → orange
        if (pen >= 1) return 0xff6fb5; // ridgeline → pink
        return 0x6dff8f; // no penalty → green
      };
      const greensByPen = new Map<number, number[]>();
      for (const id of greens) {
        if (!BOARD.nodes[id]) continue;
        const pen = vigorPenalty?.get(id) ?? 0;
        const arr = greensByPen.get(pen);
        if (arr) arr.push(id);
        else greensByPen.set(pen, [id]);
      }
      for (const [pen, ids] of greensByPen) {
        for (const id of ids) {
          const node = BOARD.nodes[id]!;
          buildLayer.circle(px(node.pixel.x), py(node.pixel.y), 7);
        }
        buildLayer.stroke({ color: ringColor(pen), width: 1.6, alpha: 0.9 });
      }
      buildLayer.visible = greens.length > 0;
      if (greens.length > 0) {
        buildLayer.alpha = 0.9;
        buildPulse = gsap.to(buildLayer, {
          alpha: 0.4,
          duration: 0.8,
          yoyo: true,
          repeat: -1,
          ease: "sine.inOut",
        });
      }

    },
    setBuildSelection(nodeId) {
      if (buildSelectPulse) {
        buildSelectPulse.kill();
        buildSelectPulse = null;
      }
      buildSelectLayer.clear();
      buildSelectPing.clear();
      const node = nodeId === null ? undefined : BOARD.nodes[nodeId];
      if (!node) {
        buildSelectLayer.visible = false;
        buildSelectPing.visible = false;
        return;
      }
      const x = px(node.pixel.x);
      const y = py(node.pixel.y);
      // The picked node must leap out of a board full of similar green rings:
      // a bigger solid spot with a heavy white rim, ringed by a dark halo (so
      // the white ring keeps contrast on the light tiles) under a bold white
      // ring, plus the animated ping below.
      const ringR = OWNER_RADIUS + 7;
      buildSelectLayer.circle(x, y, OWNER_RADIUS + 1.5).fill(0x6dff8f);
      buildSelectLayer
        .circle(x, y, OWNER_RADIUS + 1.5)
        .stroke({ color: 0xffffff, width: 2.4 });
      buildSelectLayer.circle(x, y, ringR).stroke({
        color: 0x2b2620,
        width: 5,
        alpha: 0.4,
      });
      buildSelectLayer.circle(x, y, ringR).stroke({
        color: 0xffffff,
        width: 2.6,
      });
      buildSelectLayer.visible = true;
      // Radar ping: a white ring that endlessly expands from the marker and
      // fades out — motion no candidate ring has, so the eye snaps to it.
      buildSelectPing.circle(0, 0, ringR).stroke({ color: 0xffffff, width: 2.6 });
      buildSelectPing.position.set(x, y);
      buildSelectPing.visible = true;
      const ping = { t: 0 };
      buildSelectPulse = track(
        gsap.to(ping, {
          t: 1,
          duration: 1.1,
          repeat: -1,
          ease: "sine.out",
          onUpdate: () => {
            buildSelectPing.scale.set(1 + ping.t * 1.3);
            buildSelectPing.alpha = 0.9 * (1 - ping.t);
          },
        }),
      );
    },
    setQueuedGhost(ghost) {
      queuedGhostLayer.clear();
      if (ghost === null) {
        queuedGhostLayer.visible = false;
        return;
      }
      if (ghost.kind === "building") {
        const node = BOARD.nodes[ghost.nodeId];
        if (!node) {
          queuedGhostLayer.visible = false;
          return;
        }
        const x = px(node.pixel.x);
        const y = py(node.pixel.y);
        drawBuilding(queuedGhostLayer, ghost.building, ghost.color);
        // drawBuilding draws about the origin — recentre onto the node.
        queuedGhostLayer.position.set(x, y);
        // A ring around the glyph so the ghost reads as "pending here".
        queuedGhostLayer.circle(0, 0, BUILD_R + 4).stroke({
          color: 0xffffff,
          width: 1.4,
        });
      } else {
        const na = BOARD.nodes[ghost.from];
        const nb = BOARD.nodes[ghost.to];
        if (!na || !nb) {
          queuedGhostLayer.visible = false;
          return;
        }
        queuedGhostLayer.position.set(0, 0);
        drawIBeam(queuedGhostLayer, na.pixel, nb.pixel);
        queuedGhostLayer.stroke({
          color: BRIDGE_FILL_COLOR,
          width: I_BEAM.fillWidth * HEX_SIZE,
          cap: "round",
        });
      }
      queuedGhostLayer.visible = true;
    },
    setBuildCursor(cursor) {
      const nodeCursor = cursor ?? "pointer";
      for (const g of nodeGfx) if (g) g.cursor = nodeCursor;
      // Non-node areas use the renderer's default cursor; the canvas style is a
      // belt-and-braces immediate fallback.
      app.renderer.events.cursorStyles.default = cursor ?? "inherit";
      app.canvas.style.cursor = cursor ?? "";
    },
    setHomePlacementActive(active) {
      if (homePulse) {
        homePulse.kill();
        homePulse = null;
      }
      homeLayer.clear();
      if (!active) {
        homeLayer.visible = false;
        homeSelectLayer.clear();
        homeSelectLayer.visible = false;
        return;
      }
      // Ring every free outer-tile node — the legal, unoccupied home spots.
      for (const id of FOREST_NODE_IDS) {
        const v = nodeViews[id];
        const node = BOARD.nodes[id];
        if (!v || !node || v.ownerColor !== null) continue;
        homeLayer.circle(px(node.pixel.x), py(node.pixel.y), 7);
      }
      homeLayer.stroke({ color: 0x6dff8f, width: 1.6, alpha: 0.9 });
      homeLayer.alpha = 0.9;
      homeLayer.visible = true;
      homePulse = gsap.to(homeLayer, {
        alpha: 0.35,
        duration: 0.8,
        yoyo: true,
        repeat: -1,
        ease: "sine.inOut",
      });
    },
    setHomeSelection(nodeId) {
      homeSelectLayer.clear();
      const node = nodeId === null ? undefined : BOARD.nodes[nodeId];
      if (!node) {
        homeSelectLayer.visible = false;
        return;
      }
      const x = px(node.pixel.x);
      const y = py(node.pixel.y);
      // A solid spot with a bright rim — clearly the chosen, pending node.
      homeSelectLayer.circle(x, y, OWNER_RADIUS).fill(0x6dff8f);
      homeSelectLayer.circle(x, y, OWNER_RADIUS).stroke({ color: 0xffffff, width: 1.6 });
      homeSelectLayer.circle(x, y, OWNER_RADIUS + 4).stroke({
        color: 0x6dff8f,
        width: 1.6,
        alpha: 0.9,
      });
      homeSelectLayer.alpha = 1;
      homeSelectLayer.visible = true;
    },
    isLegalHomeNode(nodeId) {
      const v = nodeViews[nodeId];
      return (
        FOREST_NODE_IDS.includes(nodeId) && !!v && v.ownerColor === null
      );
    },
    boardPointFromClient(clientX, clientY) {
      const rect = canvas.getBoundingClientRect();
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        return null;
      }
      // Canvas coords are Pixi global (stage) coords; `toLocal` then unwinds
      // viewport pan/zoom + the board tilt + the rotator spin in one go.
      const local = rotator.toLocal({
        x: clientX - rect.left,
        y: clientY - rect.top,
      });
      // Strip the render offset and normalise by hex size → the shared,
      // camera-independent board plane (origin at the board centre).
      return {
        x: (local.x - offsetX) / HEX_SIZE,
        y: (local.y - offsetY) / HEX_SIZE,
      };
    },
    showRemoteTap({ x, y, color }) {
      const root = new Container();
      root.position.set(px(x * HEX_SIZE), py(y * HEX_SIZE));
      // Constant screen size / upright / unsquashed, like the cursors — but
      // snapshotted once at creation; the ripple only lives ~a second, so it
      // doesn't chase camera changes frame-by-frame.
      const s = Math.max(
        CURSOR_MIN_SCALE,
        Math.min(CURSOR_MAX_SCALE, 1 / (viewport.scaled || 1)),
      );
      root.rotation = -rotator.rotation;
      root.scale.set(s, s / (board.scale.y || 1));
      cursorLayer.addChild(root);

      // A soft centre dot plus two staggered expanding rings — the "pulse".
      const dot = new Graphics();
      dot.circle(0, 0, 3.4).fill({ color, alpha: 0.9 });
      const rings = [new Graphics(), new Graphics()];
      for (const g of rings) {
        g.circle(0, 0, 11).stroke({ color: 0x14110d, width: 3.4, alpha: 0.35 });
        g.circle(0, 0, 11).stroke({ color, width: 1.8 });
        g.alpha = 0;
        root.addChild(g);
      }
      root.addChild(dot);

      // One driver tween renders the whole effect, so a single onComplete can
      // both untrack it and free the graphics. Deliberately NOT via track():
      // track() claims the tween's onComplete slot for its own bookkeeping.
      const RING_SPAN = 0.62; // each ring's share of the 0..1 timeline
      const RING_STAGGER = 0.3;
      const drive = { t: 0 };
      const tween = gsap.to(drive, {
        t: 1,
        duration: 1.05,
        ease: "none",
        onUpdate: () => {
          dot.alpha = 0.9 * (1 - drive.t);
          for (const [i, g] of rings.entries()) {
            const p = Math.max(
              0,
              Math.min(1, (drive.t - i * RING_STAGGER) / RING_SPAN),
            );
            const e = p * (2 - p); // ease-out
            g.scale.set(0.3 + e * 1.7);
            g.alpha = p === 0 || p === 1 ? 0 : 0.85 * (1 - p);
          }
        },
        onComplete: () => {
          liveTweens.delete(tween);
          root.destroy({ children: true });
        },
      });
      liveTweens.add(tween);
    },
    showEmoji({ x, y, emoji }) {
      const root = new Container();
      root.position.set(px(x * HEX_SIZE), py(y * HEX_SIZE));
      // Constant screen size / upright / unsquashed, snapshotted at creation
      // like the tap ripple.
      const s = Math.max(
        CURSOR_MIN_SCALE,
        Math.min(CURSOR_MAX_SCALE, 1 / (viewport.scaled || 1)),
      );
      root.rotation = -rotator.rotation;
      cursorLayer.addChild(root);

      const text = new Text({
        text: emoji,
        style: { fontSize: 34, align: "center" },
      });
      text.anchor.set(0.5);
      root.addChild(text);

      // Pulsate in (pop past full size, bounce, settle), hold a beat, then a
      // quick fade drifting upward. One driver tween renders the keyframes so
      // a single onComplete can untrack + free it (NOT via track(): track()
      // claims the tween's onComplete slot for its own bookkeeping).
      const KEYS: { t: number; scale: number; alpha: number; rise: number }[] = [
        { t: 0, scale: 0.2, alpha: 0, rise: 0 },
        { t: 0.1, scale: 1.3, alpha: 1, rise: 0 },
        { t: 0.2, scale: 0.92, alpha: 1, rise: 0 },
        { t: 0.3, scale: 1.12, alpha: 1, rise: 0 },
        { t: 0.4, scale: 1, alpha: 1, rise: 0 },
        { t: 0.75, scale: 1, alpha: 1, rise: 0 },
        { t: 1, scale: 0.85, alpha: 0, rise: 14 },
      ];
      const drive = { t: 0 };
      const tween = gsap.to(drive, {
        t: 1,
        duration: 1.5,
        ease: "none",
        onUpdate: () => {
          let a = KEYS[0]!;
          let b = KEYS[KEYS.length - 1]!;
          for (let i = 0; i < KEYS.length - 1; i++) {
            if (drive.t >= KEYS[i]!.t && drive.t <= KEYS[i + 1]!.t) {
              a = KEYS[i]!;
              b = KEYS[i + 1]!;
              break;
            }
          }
          const f = (drive.t - a.t) / (b.t - a.t || 1);
          const scale = a.scale + (b.scale - a.scale) * f;
          root.alpha = a.alpha + (b.alpha - a.alpha) * f;
          const rise = a.rise + (b.rise - a.rise) * f;
          root.scale.set(s * scale, (s / (board.scale.y || 1)) * scale);
          text.position.set(0, -rise);
        },
        onComplete: () => {
          liveTweens.delete(tween);
          root.destroy({ children: true });
        },
      });
      liveTweens.add(tween);
    },
    setRemoteCursors(cursors) {
      const now = performance.now();
      const seen = new Set<string>();
      for (const c of cursors) {
        seen.add(c.id);
        let entry = remoteCursors.get(c.id);
        // Recolour/rename (seat reassignment at game start) — rebuild the sprite.
        if (entry && (entry.color !== c.color || entry.name !== c.name)) {
          entry.root.destroy({ children: true });
          remoteCursors.delete(c.id);
          entry = undefined;
        }
        if (!entry) {
          entry = {
            root: makeCursorSprite(c.color, c.name),
            color: c.color,
            name: c.name,
            samples: [{ t: now, x: c.x, y: c.y }],
            rx: c.x,
            ry: c.y,
            vx: 0,
            vy: 0,
          };
          // Snap a brand-new cursor straight to its spot — no glide-in.
          entry.root.position.set(px(c.x * HEX_SIZE), py(c.y * HEX_SIZE));
          cursorLayer.addChild(entry.root);
          remoteCursors.set(c.id, entry);
          continue;
        }
        const last = entry.samples[entry.samples.length - 1]!;
        if (last.x !== c.x || last.y !== c.y) {
          entry.samples.push({ t: now, x: c.x, y: c.y });
          if (entry.samples.length > CURSOR_BUFFER_MAX) {
            entry.samples.splice(0, entry.samples.length - CURSOR_BUFFER_MAX);
          }
        }
      }
      for (const [id, entry] of remoteCursors) {
        if (seen.has(id)) continue;
        entry.root.destroy({ children: true });
        remoteCursors.delete(id);
      }
    },
    setNodeState(nodeId, patch) {
      const v = nodeViews[nodeId];
      if (!v) return;
      const hadOwner = v.ownerColor !== null;
      Object.assign(v, patch);
      renderNode(nodeId);
      const g = nodeGfx[nodeId];
      if (g && v.ownerColor !== null && !hadOwner) {
        track(
          gsap.fromTo(
            g.scale,
            { x: 0.4, y: 0.4 },
            { x: 1, y: 1, duration: 0.3, ease: "back.out(2)" },
          ),
        );
      }
    },
    nodeScreenPosition(nodeId) {
      const g = nodeGfx[nodeId];
      if (!g) return null;
      // Global = canvas space (all board transforms applied); shift by the
      // canvas's viewport offset to get client coordinates.
      const pos = g.getGlobalPosition();
      const rect = app.canvas.getBoundingClientRect();
      return { x: rect.left + pos.x, y: rect.top + pos.y };
    },
    setSeveredEdges(keys) {
      severedEdges.clear();
      for (const key of keys) severedEdges.add(key);
      redrawEdges(); // drop the cut edges from the grid…
      redrawSevered(); // …and draw their "T" barriers
    },
    setBridges(keys) {
      bridgeEdges.clear();
      for (const key of keys) bridgeEdges.add(key);
      redrawBridges();
    },
    setAims(list) {
      aims = list;
      redrawAims();
    },
    setBridgeHighlight(allowed, tooCostly) {
      if (bridgePulse) {
        bridgePulse.kill();
        bridgePulse = null;
      }
      bridgeHints.removeChildren().forEach((c) => c.destroy());
      const greens = allowed ?? [];
      const greys = tooCostly ?? [];
      if (greens.length === 0 && greys.length === 0) {
        bridgeHints.visible = false;
        return;
      }
      bridgeHints.visible = true;
      bridgeHints.alpha = 1;
      const HIT = 8; // tap tolerance (board px) from the path
      const drawHint = (
        a: number,
        b: number,
        color: number,
        alpha: number,
        interactive: boolean,
      ) => {
        const na = BOARD.nodes[a];
        const nb = BOARD.nodes[b];
        if (!na || !nb) return;
        const ax = px(na.pixel.x);
        const ay = py(na.pixel.y);
        const bx = px(nb.pixel.x);
        const by = py(nb.pixel.y);
        const dx = bx - ax;
        const dy = by - ay;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len;
        const uy = dy / len;
        const inset = Math.min(len * 0.18, 7);
        const eg = new Graphics();
        eg.moveTo(ax + ux * inset, ay + uy * inset).lineTo(
          bx - ux * inset,
          by - uy * inset,
        );
        eg.stroke({ color, width: 4, alpha, cap: "round" });
        if (interactive) {
          eg.eventMode = "static";
          eg.cursor = "pointer";
          eg.hitArea = {
            contains: (x: number, y: number) =>
              segDist(x, y, ax, ay, bx, by) <= HIT,
          };
          eg.on("pointertap", () => {
            if (!tapDragged) onEdgeClick?.(a, b);
          });
        }
        bridgeHints.addChild(eg);
      };
      for (const [a, b] of greys) drawHint(a, b, 0x9a948c, 0.5, false);
      // Eligible paths in the same green as the no-penalty (0-altitude) build ring.
      for (const [a, b] of greens) drawHint(a, b, 0x6dff8f, 0.95, true);
      if (greens.length > 0) {
        bridgePulse = gsap.to(bridgeHints, {
          alpha: 0.5,
          duration: 0.8,
          yoyo: true,
          repeat: -1,
          ease: "sine.inOut",
        });
      }
    },
    applyPlacements() {
      // Tear down any previous reveal + regions.
      if (revealTick) {
        app.ticker.remove(revealTick);
        revealTick = null;
      }
      regions.removeChildren().forEach((c) => c.destroy());

      // Build a drawable for every slot, ordered centre → outward so the board
      // tiles in from the middle. Each tile is the UNION of its nodes' lattice
      // triangles (a node sits at the centroid of its three hexes, so its
      // triangle's corners are those three hex CENTRES). Tile edges therefore
      // run along hex-centre lines — through the middle between hexes, never on
      // the corner nodes — and neighbouring tiles tessellate exactly, like the
      // physical board's diamonds / trapezoids / triangles.
      type P = { x: number; y: number };
      const centre: P = { x: px(0), y: py(0) };
      const built = SLOTS.map((slot) => {
        const nodes = slot.nodeIds
          .map((id) => BOARD.nodes[id])
          .filter((n): n is NonNullable<typeof n> => !!n);
        const tris: [P, P, P][] = nodes.map((n) => {
          const [h0, h1, h2] = n.hexes;
          const c0 = hexToPixel(LAYOUT, h0);
          const c1 = hexToPixel(LAYOUT, h1);
          const c2 = hexToPixel(LAYOUT, h2);
          return [
            { x: px(c0.x), y: py(c0.y) },
            { x: px(c1.x), y: py(c1.y) },
            { x: px(c2.x), y: py(c2.y) },
          ];
        });
        let sx = 0;
        let sy = 0;
        let count = 0;
        for (const t of tris)
          for (const p of t) {
            sx += p.x;
            sy += p.y;
            count++;
          }
        const cx = count ? sx / count : centre.x;
        const cy = count ? sy / count : centre.y;
        const dist = Math.hypot(cx - centre.x, cy - centre.y);
        return { slot, tris, cx, cy, dist };
      })
        .filter((t) => t.tris.length > 0)
        .sort((a, b) => a.dist - b.dist);

      interface RevealItem {
        gfx: Graphics;
        delay: number;
      }
      const items: RevealItem[] = built.map(({ slot, tris, cx, cy }, rank) => {
        const base = ZONE_COLORS[slot.zone];
        const gfx = new Graphics();
        // Per-triangle directional shading (light from the top-left) gives each
        // tile a faceted, faintly 3D look like the reference art — no gradient
        // fills needed. Normalise the ramp by the tile's local radius.
        const local = tris.map(([a, b, c]) => ({
          pts: [a.x - cx, a.y - cy, b.x - cx, b.y - cy, c.x - cx, c.y - cy],
          lx: (a.x + b.x + c.x) / 3 - cx,
          ly: (a.y + b.y + c.y) / 3 - cy,
        }));
        let radius = 1;
        for (const t of local) radius = Math.max(radius, Math.hypot(t.lx, t.ly));
        const LDX = -0.55; // light direction: up-left
        const LDY = -0.83;
        const AMP = 0.16; // ±16% brightness across the tile
        for (const t of local) {
          const f = 1 + (AMP * (t.lx * LDX + t.ly * LDY)) / radius;
          gfx
            .poly(t.pts)
            .fill({ color: shade(base, f), alpha: REGION_FILL_ALPHA });
        }
        // Outline = lattice edges belonging to exactly one of the tile's
        // triangles (the tile's silhouette), drawn along hex-centre lines.
        const edges = new Map<string, { a: P; b: P; n: number }>();
        const addEdge = (a: P, b: P) => {
          const ka = `${Math.round(a.x)},${Math.round(a.y)}`;
          const kb = `${Math.round(b.x)},${Math.round(b.y)}`;
          const key = ka < kb ? `${ka}|${kb}` : `${kb}|${ka}`;
          const e = edges.get(key);
          if (e) e.n++;
          else edges.set(key, { a, b, n: 1 });
        };
        for (const [a, b, c] of tris) {
          addEdge(a, b);
          addEdge(b, c);
          addEdge(c, a);
        }
        for (const { a, b, n } of edges.values()) {
          if (n !== 1) continue;
          gfx.moveTo(a.x - cx, a.y - cy).lineTo(b.x - cx, b.y - cy);
        }
        gfx.stroke({
          color: TILE_BORDER,
          alpha: REGION_LINE_ALPHA,
          width: 2.5,
          join: "round",
          cap: "round",
        });
        gfx.position.set(cx, cy);
        gfx.eventMode = "static";
        gfx.on("pointertap", () => {
          if (!tapDragged) onTileTap(slot.id);
        });
        gfx.alpha = 0;
        gfx.scale.set(0.5);
        regions.addChild(gfx);

        return { gfx, delay: rank * REVEAL_STEP_MS };
      });

      // Drive the staggered reveal off Pixi's ticker (always running), so tiles
      // reliably appear and finish fully visible regardless of GSAP.
      let elapsed = 0;
      const tick = (ticker: { deltaMS: number }) => {
        elapsed += ticker.deltaMS;
        let allDone = true;
        for (const it of items) {
          const t = Math.max(0, Math.min(1, (elapsed - it.delay) / REVEAL_DUR_MS));
          if (t < 1) allDone = false;
          const e = easeOutBack(t);
          it.gfx.scale.set(0.5 + 0.5 * e);
          it.gfx.alpha = t;
        }
        if (allDone) {
          for (const it of items) {
            it.gfx.scale.set(1);
            it.gfx.alpha = 1;
          }
          if (revealTick) app.ticker.remove(revealTick);
          revealTick = null;
        }
      };
      revealTick = tick;
      app.ticker.add(tick);
    },
    destroy() {
      window.removeEventListener("resize", handleResize);
      viewport.off("drag-start", clearTileSelection);
      viewport.off("pinch-start", clearTileSelection);
      viewport.off("wheel-start", clearTileSelection);
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerEnd);
      canvas.removeEventListener("pointercancel", onPointerEnd);
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("lostpointercapture", onLostCapture);
      if (homePulse) homePulse.kill();
      if (buildPulse) buildPulse.kill();
      if (bridgePulse) bridgePulse.kill();
      // Kill in-flight tweens before Pixi frees their targets.
      for (const t of liveTweens) t.kill();
      liveTweens.clear();
      app.destroy(true, { children: true });
    },
  };
}
